import { matchPath } from 'react-router-dom'
import api from './enhancedApiService'
import { ANALYTICS_EVENT_TYPES } from '@config/analyticsEventTypes'
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'
import { sanitizeTelemetryPayload } from '@/privacy/telemetrySanitizer'

export const ADMIN_CONTENT_MIGRATION_MODES = Object.freeze({
  COMPATIBILITY: 'compatibility',
  NATIVE: 'native',
  DISABLED: 'disabled'
})

const VALID_MIGRATION_MODES = new Set(
  Object.values(ADMIN_CONTENT_MIGRATION_MODES)
)
const LEGACY_ROUTE_TELEMETRY_TIMEOUT_MS = 750

const MIGRATED_ROUTES = Object.freeze([
  {
    id: 'admin-home',
    legacyPath: '/blog/admin',
    legacyTemplate: '/blog/admin',
    dashboardTemplate: '/admin',
    buildDashboardPath: () => '/admin'
  },
  {
    id: 'post-new',
    legacyPath: '/blog/admin/posts/new',
    legacyTemplate: '/blog/admin/posts/new',
    dashboardTemplate: '/admin/content/posts/new',
    buildDashboardPath: () => '/admin/content/posts/new'
  },
  {
    id: 'post-edit',
    legacyPath: '/blog/admin/posts/:postId/edit',
    legacyTemplate: '/blog/admin/posts/:postId/edit',
    dashboardTemplate: '/admin/content/posts/:postId/edit',
    buildDashboardPath: params =>
      `/admin/content/posts/${encodeURIComponent(
        decodePathSegment(params.postId)
      )}/edit`
  },
  {
    id: 'posts',
    legacyPath: '/blog/admin/posts',
    legacyTemplate: '/blog/admin/posts',
    dashboardTemplate: '/admin/content/posts',
    buildDashboardPath: () => '/admin/content/posts'
  },
  {
    id: 'comments',
    legacyPath: '/blog/admin/comments',
    legacyTemplate: '/blog/admin/comments',
    dashboardTemplate: '/admin/content/comments',
    buildDashboardPath: () => '/admin/content/comments'
  },
  {
    id: 'ai-queue',
    legacyPath: '/blog/admin/ai-queue',
    legacyTemplate: '/blog/admin/ai-queue',
    dashboardTemplate: '/admin/content/ai-queue',
    buildDashboardPath: () => '/admin/content/ai-queue'
  },
  {
    id: 'ai-schedules',
    legacyPath: '/blog/admin/ai-schedules',
    legacyTemplate: '/blog/admin/ai-schedules',
    dashboardTemplate: '/admin/content/schedules',
    buildDashboardPath: () => '/admin/content/schedules'
  },
  {
    id: 'analytics',
    legacyPath: '/blog/admin/analytics',
    legacyTemplate: '/blog/admin/analytics',
    dashboardTemplate: '/admin/content/analytics',
    buildDashboardPath: () => '/admin/content/analytics'
  },
  {
    id: 'settings',
    legacyPath: '/blog/admin/settings',
    legacyTemplate: '/blog/admin/settings',
    dashboardTemplate: '/admin/content/settings',
    buildDashboardPath: () => '/admin/content/settings'
  },
  {
    id: 'admin-fallback',
    legacyPath: '/blog/admin/*',
    legacyTemplate: '/blog/admin/*',
    dashboardTemplate: '/admin',
    buildDashboardPath: () => '/admin'
  }
])

function decodePathSegment(value) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function getResponseCode(error) {
  return (
    error?.response?.data?.code ||
    error?.response?.data?.error?.code ||
    error?.code ||
    null
  )
}

export function resolveLegacyAdminContentRoute(pathname) {
  for (const route of MIGRATED_ROUTES) {
    const match = matchPath(
      {
        path: route.legacyPath,
        end: true,
        caseSensitive: false
      },
      pathname
    )
    if (match) {
      return {
        ...route,
        key: `${route.id}:${match.pathname}`,
        params: match.params
      }
    }
  }

  return null
}

export function buildAdminContentDashboardUrl(
  route,
  dashboardBaseUrl = import.meta.env.VITE_DASHBOARD_URL,
  { search = '' } = {}
) {
  if (!route?.buildDashboardPath) {
    throw new Error('A migrated content route is required')
  }
  if (!dashboardBaseUrl) {
    throw new Error('The dashboard URL is not configured')
  }

  const baseUrl = new URL(dashboardBaseUrl)
  const destination = new URL(
    route.buildDashboardPath(route.params || {}),
    baseUrl
  )
  destination.search = search
  return destination.toString()
}

export async function getAdminContentMigrationMode(options = {}) {
  const response = await api.get('/admin/session', options)
  const session = response?.data?.data
  const mode = session?.features?.contentMigration

  if (!VALID_MIGRATION_MODES.has(mode)) {
    const error = new Error(
      'Admin session returned an invalid content migration mode'
    )
    error.code = 'INVALID_CONTENT_MIGRATION_MODE'
    throw error
  }

  return {
    mode,
    session
  }
}

export function isAdminContentMigrationDisabledError(error) {
  return (
    getResponseCode(error) === 'ADMIN_CONSOLE_DISABLED'
  )
}

export async function trackLegacyAdminContentRouteUse(route) {
  if (!route || !isPrivacyCategoryAllowed('analytics')) {
    return false
  }

  const metadata = sanitizeTelemetryPayload({
    legacyRouteTemplate: route.legacyTemplate,
    destinationRouteTemplate: route.dashboardTemplate,
    migrationMode: ADMIN_CONTENT_MIGRATION_MODES.NATIVE,
    surface: 'blog-admin'
  })

  try {
    await api.post(
      '/analytics/ingest',
      {
        eventType: ANALYTICS_EVENT_TYPES.ADMIN_CONTENT_LEGACY_ROUTE_USED,
        metadata
      },
      {
        timeout: LEGACY_ROUTE_TELEMETRY_TIMEOUT_MS
      }
    )
    return true
  } catch {
    return false
  }
}

export function replaceWithAdminContentRoute(destination) {
  window.location.replace(destination)
}
